import React from 'react';
import { Document, Page, Text, View, StyleSheet, Link, Image } from '@react-pdf/renderer';
import {
  commonStyles,
  formatDate,
  formatPhoneNumber,
  parseHtmlToText,
  renderBulletPoints,
  isValidSummary
} from '../Services/ReactPDFService.jsx';

// Photo Template Styles
const styles = StyleSheet.create({
  ...commonStyles,
  page: {
    ...commonStyles.page,
    backgroundColor: '#ffffff',
    paddingTop: 0,
    paddingBottom: 30,
    paddingHorizontal: 0,
  },

  // Header Section
  header: {
    backgroundColor: '#14324C',
    padding: '20px 30px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  headerLeft: {
    flex: 1,
  },
  name: {
    fontSize: 24,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: 15,
    textTransform: 'uppercase',
  },
  professionalTitle: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: 400,
    marginBottom: 12,
  },
  contactInfo: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  contactItem: {
    fontSize: 9,
    color: '#ffffff',
    marginRight: 12,
  },
  profilePhotoContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    border: '3px solid #ffffff',
  },
  profilePhoto: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },

  // Two Column Layout
  contentContainer: {
    flexDirection: 'row',
    paddingHorizontal: 30,
    gap: 20,
  },
  leftColumn: {
    width: '60%',
  },
  rightColumn: {
    width: '40%',
  },

  // Section Styles
  sectionContainer: {
    marginBottom: 15,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#3C6C93',
    marginBottom: 8,
    textTransform: 'uppercase',
    borderBottom: '2px solid #3C6C93',
    paddingBottom: 3,
  },

  // Summary Section
  summaryText: {
    fontSize: 10,
    lineHeight: 1.5,
    color: '#000000',
    textAlign: 'justify',
  },

  // Experience Section
  experienceItem: {
    marginBottom: 12,
  },
  jobTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#3C6C93',
    marginBottom: 2,
  },
  company: {
    fontSize: 10,
    fontWeight: 700,
    color: '#3C6C93',
    marginBottom: 2,
  },
  dateLocation: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 4,
    fontStyle: 'italic',
  },
  description: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#000000',
  },

  // Education Section
  educationItem: {
    marginBottom: 10,
  },
  degree: {
    fontSize: 11,
    fontWeight: 700,
    color: '#3C6C93',
    marginBottom: 2,
  },
  university: {
    fontSize: 10,
    fontWeight: 700,
    color: '#3C6C93',
    marginBottom: 2,
  },
  educationDate: {
    fontSize: 9,
    color: '#666666',
    marginBottom: 2,
    fontStyle: 'italic',
  },
  gpa: {
    fontSize: 9,
    color: '#666666',
  },

  // Projects Section
  projectItem: {
    marginBottom: 10,
  },
  projectName: {
    fontSize: 11,
    fontWeight: 700,
    color: '#3C6C93',
    marginBottom: 3,
  },
  projectTech: {
    fontSize: 9,
    color: '#666666',
    fontStyle: 'italic',
    marginBottom: 3,
  },
  projectDescription: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#000000',
  },

  // Skills Section
  skillsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  skillItem: {
    fontSize: 10,
    fontWeight: 700,
    color: '#000000',
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#f0f0f0',
    borderRadius: 3,
    marginBottom: 4,
  },

  // Certifications Section
  certificationItem: {
    marginBottom: 8,
  },
  certificationName: {
    fontSize: 10,
    fontWeight: 700,
    color: '#3C6C93',
    marginBottom: 2,
  },
  certificationAuthority: {
    fontSize: 9,
    color: '#666666',
  },
  certificationDate: {
    fontSize: 9,
    color: '#666666',
    fontStyle: 'italic',
  },

  // Achievements Section
  achievementItem: {
    marginBottom: 6,
  },
  achievementText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#000000',
    fontWeight: 700,
  },
  achievementBullet: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  bulletSymbol: {
    fontSize: 10,
    color: '#000000',
    marginRight: 6,
    fontWeight: 700,
  },
  bulletText: {
    fontSize: 10,
    lineHeight: 1.4,
    color: '#000000',
    fontWeight: 700,
    flex: 1,
  },
});

// Helper function to safely extract skill name from string or object
const getSkillName = (skill) => {
  if (typeof skill === "string") return skill;
  if (skill && typeof skill === "object") return skill.name || skill.skill || "";
  return "";
};

// Helper to get language name from string or object
const getLanguageName = (lang) => {
  if (typeof lang === "string") return lang;
  if (lang && typeof lang === "object") return lang.name || lang.language || "";
  return "";
};

// Helper to get interest name from string or object
const getInterestName = (interest) => {
  if (typeof interest === "string") return interest;
  if (interest && typeof interest === "object") return interest.name || interest.title || "";
  return "";
};

const PhotoTemplate = ({ resumeData, customStyles = {}, customSectionOrder = null }) => {
  const {
    personalInfo = {},
    workExperience = [],
    education = [],
    skills = {},
    projects = [],
    certifications = [],
    achievements = [],
    languages = [],
    awards = [],
    summary = '',
    professionalTitle = '',
    profilePhoto = {},
    customSections = [],
    sectionTitles = {},
    // New sections
    volunteering = [],
    interests = [],
    industryExpertise = [],
    publications = [],
    references = [],
  } = resumeData || {};

  // Default section titles
  const defaultTitles = {
    summary: 'Professional Summary',
    experience: 'Work Experience',
    education: 'Education',
    skills: 'Skills',
    projects: 'Projects',
    certifications: 'Certifications',
    achievements: 'Achievements',
    languages: 'Languages',
    awards: 'Awards',
    // New sections
    volunteering: 'Volunteering',
    interests: 'Interests',
    industryExpertise: 'Industry Expertise',
    publications: 'Publications',
    references: 'References',
  };

  // Get section title with fallback to default
  const getSectionTitle = (key) => sectionTitles[key] || defaultTitles[key];

  // Get profile photo URL - check both profilePhoto.url and personalInfo.profilePhotoUrl
  const profilePhotoUrl = profilePhoto?.url || personalInfo?.profilePhotoUrl || null;

  // Get latest work experience for header
  const latestJob = workExperience && workExperience.length > 0 ? workExperience[0] : null;
  const headerTitle = professionalTitle || (latestJob ? latestJob.jobTitle : '');

  // ONE-PAGE RESUME LIMITS - Trim content to fit in one page

  // Latest 2 work experiences only
  const limitedWorkExperience = workExperience.slice(0, 2);

  // Latest 2 education entries only
  const limitedEducation = education.slice(0, 2);

  // Latest 2 projects only
  const limitedProjects = projects.slice(0, 2);

  // Latest 2 certifications only
  const limitedCertifications = certifications.slice(0, 2);

  // Latest 2 achievements only
  const limitedAchievements = achievements.slice(0, 2);

  // Latest 3 languages only
  const limitedLanguages = languages.slice(0, 3);

  // Latest 2 awards only
  const limitedAwards = awards.slice(0, 2);

  // New sections - limited for one-page resume
  const limitedVolunteering = volunteering.slice(0, 2);
  const limitedInterests = interests.slice(0, 4);
  const limitedIndustryExpertise = industryExpertise.slice(0, 3);
  const limitedPublications = publications.slice(0, 2);
  const limitedReferences = references.slice(0, 2);

  // Combine all skills into one array for display - limit to first 6 skills
  const allSkills = (skills.list || [
    ...(skills.technical || []),
    ...(skills.soft || []),
    ...(skills.tools || []),
    ...(skills.languages || []),
    ...(skills.frameworks || []),
    ...(skills.developer_tools || []),
    ...(skills.other || [])
  ]).slice(0, 6);

  // Limit bullet points in experience descriptions
  // First experience: 2 bullet points, Second experience: 3 bullet points
  const limitExperienceBulletPoints = (description, index) => {
    if (!description) return null;

    const textString = typeof description === 'string' ? description : String(description || '');
    const points = parseHtmlToText(textString)
      .split('\n')
      .filter(point => point.trim());

    // First experience: 2 bullets, Second experience: 3 bullets
    const maxBullets = index === 0 ? 2 : 3;
    const limitedPoints = points.slice(0, maxBullets);

    return limitedPoints.join('\n');
  };

  // Limit project description to 1 bullet point only
  const limitProjectDescription = (description) => {
    if (!description) return null;

    const textString = typeof description === 'string' ? description : String(description || '');
    const points = parseHtmlToText(textString)
      .split('\n')
      .filter(point => point.trim())
      .map(point => point.replace(/^[•\-\*]\s*/, '')); // Remove existing bullet points

    // Only 1 bullet point per project
    return points.length > 0 ? points[0] : null;
  };

  // Limit technologies to first 5 only
  const limitTechnologies = (technologies) => {
    if (!technologies) return null;

    const techArray = technologies.split(',').map(t => t.trim());
    return techArray.slice(0, 5).join(', ');
  };

  // Render Skills in 2-column grid with dynamic wrapping
  const renderSkills = () => {
    if (allSkills.length === 0) return null;

    return (
      <View style={styles.skillsGrid}>
        {allSkills.map((skill, index) => (
          <Text key={index} style={styles.skillItem}>
            {getSkillName(skill)}
          </Text>
        ))}
      </View>
    );
  };

  // Render Achievements with bold bullet points - limited to 2 achievements
  const renderAchievements = () => {
    if (!limitedAchievements || limitedAchievements.length === 0) return null;

    return limitedAchievements.map((achievement, index) => {
      if (!achievement.description) return null;

      const points = parseHtmlToText(achievement.description)
        .split('\n')
        .filter(point => point.trim());

      return points.map((point, pointIndex) => (
        <View key={`${index}-${pointIndex}`} style={styles.achievementBullet}>
          <Text style={styles.bulletSymbol}>•</Text>
          <Text style={styles.bulletText}>{point}</Text>
        </View>
      ));
    });
  };

  return (
    <Document>
      <Page size="A4" style={customStyles?.fontFamily ? { ...styles.page, fontFamily: customStyles.fontFamily } : styles.page} wrap={true}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.headerLeft}>
            {/* Full Name */}
            <Text style={styles.name}>
              {personalInfo.firstName} {personalInfo.lastName}
            </Text>

            {/* Professional Title / Latest Job Title */}
            {headerTitle && (
              <Text style={styles.professionalTitle}>{headerTitle}</Text>
            )}

            {/* Contact Information */}
            <View style={styles.contactInfo}>
              {personalInfo.phone && (
                <Text style={styles.contactItem}>
                  • {formatPhoneNumber(personalInfo.phone)}
                </Text>
              )}
              {personalInfo.email && (
                <Text style={styles.contactItem}>
                  • {personalInfo.email}
                </Text>
              )}
              {personalInfo.linkedinUrl && (
                <Text style={styles.contactItem}>
                  • {personalInfo.linkedinUrl.split('/').pop()}
                </Text>
              )}
              {personalInfo.location && (
                <Text style={styles.contactItem}>
                  • {personalInfo.location}
                </Text>
              )}
            </View>
          </View>

          {/* Profile Photo */}
          {profilePhotoUrl && (
            <View style={styles.profilePhotoContainer}>
              <Image
                src={profilePhotoUrl}
                style={styles.profilePhoto}
              />
            </View>
          )}
        </View>

        {/* Two Column Layout */}
        <View style={styles.contentContainer}>
          {/* Left Column */}
          <View style={styles.leftColumn}>
            {/* Summary Section */}
            {summary && isValidSummary(summary) && (
              <View style={styles.sectionContainer} wrap={false}>
                <Text style={styles.sectionTitle}>{getSectionTitle('summary').toUpperCase()}</Text>
                <Text style={styles.summaryText}>
                  {parseHtmlToText(summary)}
                </Text>
              </View>
            )}

            {/* Experience Section */}
            {limitedWorkExperience && limitedWorkExperience.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('experience').toUpperCase()}</Text>
                {limitedWorkExperience.map((job, index) => (
                  <View key={index} style={styles.experienceItem} wrap={false}>
                    <Text style={styles.jobTitle}>{job.jobTitle}</Text>
                    <Text style={styles.company}>{job.company}</Text>
                    <Text style={styles.dateLocation}>
                      {formatDate(job.startDate)} - {(job.isCurrentJob || job.endDate === 'Present' || job.endDate === 'Current' || !job.endDate) ? 'Present' : formatDate(job.endDate)}
                      {job.location && ` • ${job.location}`}
                    </Text>
                    {job.description && renderBulletPoints(limitExperienceBulletPoints(job.description, index), styles)}
                  </View>
                ))}
              </View>
            )}

            {/* Education Section */}
            {limitedEducation && limitedEducation.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('education').toUpperCase()}</Text>
                {limitedEducation.map((edu, index) => (
                  <View key={index} style={styles.educationItem} wrap={false}>
                    <Text style={styles.degree}>
                      {edu.degree} {edu.fieldOfStudy && `in ${edu.fieldOfStudy}`}
                    </Text>
                    <Text style={styles.university}>{edu.school}</Text>
                    <Text style={styles.educationDate}>
                      {formatDate(edu.startDate)} - {(edu.endDate === 'Present' || edu.endDate === 'Current' || !edu.endDate) ? 'Present' : formatDate(edu.endDate)}
                      {edu.location && ` • ${edu.location}`}
                      {edu.gpa && ` • GPA: ${edu.gpa}`}
                    </Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Right Column */}
          <View style={styles.rightColumn}>
            {/* Projects Section */}
            {limitedProjects && limitedProjects.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('projects').toUpperCase()}</Text>
                {limitedProjects.map((project, index) => {
                  const limitedDesc = limitProjectDescription(project.description);
                  return (
                    <View key={index} style={styles.projectItem} wrap={false}>
                      <Text style={styles.projectName}>{project.name}</Text>
                      {project.technologies && (
                        <Text style={styles.projectTech}>
                          Technologies: {limitTechnologies(project.technologies)}
                        </Text>
                      )}
                      {limitedDesc && (
                        <View style={{ flexDirection: 'row', marginTop: 4 }}>
                          <Text style={{ fontSize: 10, color: '#000000', marginRight: 6 }}>•</Text>
                          <Text style={{ fontSize: 10, lineHeight: 1.4, color: '#000000', flex: 1 }}>
                            {limitedDesc}
                          </Text>
                        </View>
                      )}
                    </View>
                  );
                })}
              </View>
            )}

            {/* Skills Section */}
            {allSkills.length > 0 && (
              <View style={styles.sectionContainer} wrap={false}>
                <Text style={styles.sectionTitle}>{getSectionTitle('skills').toUpperCase()}</Text>
                {renderSkills()}
              </View>
            )}

            {/* Certifications Section */}
            {limitedCertifications && limitedCertifications.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('certifications').toUpperCase()}</Text>
                {limitedCertifications.map((cert, index) => (
                  <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }} wrap={false}>
                    <Text style={{ fontSize: 9, color: '#3C6C93', marginRight: 5, lineHeight: 1.5 }}>•</Text>
                    <Text style={{ fontSize: 9, color: '#333333', lineHeight: 1.5, flex: 1 }}>
                      {cert.name}
                      {(cert.authority || cert.issuer) && ` - ${cert.authority || cert.issuer}`}
                      {cert.date && ` (${formatDate(cert.date)})`}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* Achievements Section */}
            {limitedAchievements && limitedAchievements.length > 0 && (
              <View style={styles.sectionContainer} wrap={false}>
                <Text style={styles.sectionTitle}>{getSectionTitle('achievements').toUpperCase()}</Text>
                <View style={styles.achievementItem}>
                  {renderAchievements()}
                </View>
              </View>
            )}

            {/* Languages Section */}
            {limitedLanguages && limitedLanguages.length > 0 && (
              <View style={styles.sectionContainer} wrap={false}>
                <Text style={styles.sectionTitle}>{getSectionTitle('languages').toUpperCase()}</Text>
                <View style={styles.skillsGrid}>
                  {limitedLanguages.map((lang, index) => (
                    <Text key={index} style={styles.skillItem}>
                      {getLanguageName(lang)}{lang.proficiency ? ` - ${lang.proficiency}` : ''}
                    </Text>
                  ))}
                </View>
              </View>
            )}

            {/* Awards Section */}
            {limitedAwards && limitedAwards.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('awards').toUpperCase()}</Text>
                {limitedAwards.map((award, index) => (
                  <View key={index} style={styles.certificationItem} wrap={false}>
                    <Text style={styles.certificationName}>{award.title}</Text>
                    <Text style={styles.certificationAuthority}>
                      {award.issuer}
                      {award.date && ` • ${formatDate(award.date)}`}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* Volunteering Section */}
            {limitedVolunteering && limitedVolunteering.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('volunteering').toUpperCase()}</Text>
                {limitedVolunteering.map((vol, index) => (
                  <View key={index} style={styles.certificationItem} wrap={false}>
                    <Text style={styles.certificationName}>{vol.role}</Text>
                    <Text style={styles.certificationAuthority}>
                      {vol.organization}
                      {(vol.startDate || vol.endDate) && ` • ${formatDate(vol.startDate)} - ${vol.isCurrentRole ? 'Present' : formatDate(vol.endDate)}`}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* Interests Section */}
            {limitedInterests && limitedInterests.length > 0 && (
              <View style={styles.sectionContainer} wrap={false}>
                <Text style={styles.sectionTitle}>{getSectionTitle('interests').toUpperCase()}</Text>
                <View style={styles.skillsGrid}>
                  {limitedInterests.map((interest, index) => (
                    <Text key={index} style={styles.skillItem}>
                      {getInterestName(interest)}
                    </Text>
                  ))}
                </View>
              </View>
            )}

            {/* Industry Expertise Section */}
            {limitedIndustryExpertise && limitedIndustryExpertise.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('industryExpertise').toUpperCase()}</Text>
                {limitedIndustryExpertise.map((exp, index) => (
                  <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }} wrap={false}>
                    <Text style={{ fontSize: 9, color: '#3C6C93', marginRight: 5, lineHeight: 1.5 }}>•</Text>
                    <Text style={{ fontSize: 9, color: '#333333', lineHeight: 1.5, flex: 1 }}>
                      {exp.name}{exp.proficiency ? ` - ${exp.proficiency}` : ''}{exp.yearsOfExperience ? ` (${exp.yearsOfExperience} yrs)` : ''}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* Publications Section */}
            {limitedPublications && limitedPublications.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('publications').toUpperCase()}</Text>
                {limitedPublications.map((pub, index) => (
                  <View key={index} style={{ flexDirection: 'row', marginBottom: 4 }} wrap={false}>
                    <Text style={{ fontSize: 9, color: '#3C6C93', marginRight: 5, lineHeight: 1.5 }}>•</Text>
                    <Text style={{ fontSize: 9, color: '#333333', lineHeight: 1.5, flex: 1 }}>
                      {pub.title}{pub.journal ? ` - ${pub.journal}` : ''}{pub.date ? ` (${formatDate(pub.date)})` : ''}
                    </Text>
                  </View>
                ))}
              </View>
            )}

            {/* References Section */}
            {limitedReferences && limitedReferences.length > 0 && (
              <View style={styles.sectionContainer}>
                <Text style={styles.sectionTitle}>{getSectionTitle('references').toUpperCase()}</Text>
                {limitedReferences.map((ref, index) => (
                  <View key={index} style={{ marginBottom: 6 }} wrap={false}>
                    <Text style={{ fontSize: 9, color: '#333333', fontWeight: 'bold', lineHeight: 1.5 }}>
                      {ref.name}
                    </Text>
                    {(ref.title || ref.company) && (
                      <Text style={{ fontSize: 8, color: '#666666', lineHeight: 1.4 }}>
                        {ref.title}{ref.title && ref.company ? ' - ' : ''}{ref.company}
                      </Text>
                    )}
                    {(ref.email || ref.phone) && (
                      <Text style={{ fontSize: 8, color: '#666666', lineHeight: 1.4 }}>
                        {[ref.email, ref.phone].filter(Boolean).join(' • ')}
                      </Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* Custom Sections */}
            {customSections && customSections.length > 0 && customSections.map((customSection) => (
              customSection.id && customSection.title && customSection.items?.length > 0 && (
                <View key={customSection.id} style={styles.sectionContainer} wrap={false}>
                  <Text style={styles.sectionTitle}>{customSection.title.toUpperCase()}</Text>
                  {customSection.items.map((item, itemIndex) => (
                    <View key={itemIndex} style={{ flexDirection: 'row', marginBottom: 4 }}>
                      <Text style={{ fontSize: 9, color: '#3C6C93', marginRight: 5, lineHeight: 1.5 }}>•</Text>
                      <Text style={{ fontSize: 9, color: '#333333', lineHeight: 1.5, flex: 1 }}>
                        {item.content}
                      </Text>
                    </View>
                  ))}
                </View>
              )
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default PhotoTemplate;
